import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { useIncidents } from 'context/IncidentsContext';

// ---------------------------------------------------------------------------
// GetStartedGuide
//
// A big, plain-English "here's what to do next" card that renders on the
// Dashboard for a first-time (or nearly-empty) workspace. It replaces the
// pattern where a new teacher lands on a page full of zeroes and doesn't
// know which button to click first.
//
// Progression:
//   0 students                  → "Add your first student"
//   ≥ 1 student, 0 goals        → "Add an IEP goal for that student"
//   ≥ 1 goal, 0 data + 0 incs   → "Start logging data"
//   any data                    → don't render (teacher is off and running)
//
// Copy is intentionally short and second-person ("you"). No app jargon —
// no "records", no "entries", no "workspace". A busy teacher scanning
// during their planning period should be able to read this in five
// seconds and know their next click.
// ---------------------------------------------------------------------------

const GetStartedGuide = ({ onAddStudentClick, onCollectDataClick }) => {
  const navigate = useNavigate();
  const { students } = useStudents();
  const { goals } = useIEPGoals();
  const { incidents } = useIncidents();

  const studentCount = students.length;
  const goalCount = goals.length;
  const incidentCount = incidents.length;
  const dataPointCount = goals.reduce(
    (sum, g) => sum + (Array.isArray(g.progressEntries) ? g.progressEntries.length : 0),
    0,
  );

  // The guide auto-hides ONLY when all three steps are done at least
  // once — you have a student, a goal, and any evidence of logging
  // (either a data point OR a behavior incident). This is a
  // deliberate change from "hide as soon as ANY data exists" because
  // in demo mode a preloaded incident from a previous session would
  // hide the guide entirely, leaving a fresh user staring at an
  // empty dashboard.
  const allStepsDone =
    studentCount > 0 &&
    goalCount > 0 &&
    (dataPointCount > 0 || incidentCount > 0);
  if (allStepsDone) return null;

  const steps = [
    {
      n: 1,
      title: 'Add a student',
      body: "Start with just their name — the rest can wait. You'll only do this once per student, at the start of the year or when a new student joins your caseload.",
      done: studentCount > 0,
      cta: studentCount > 0 ? null : {
        label: 'Add your first student',
        icon: 'Plus',
        onClick: onAddStudentClick,
      },
    },
    {
      n: 2,
      title: 'Write an IEP goal for that student',
      body: "One goal is enough to get started. You can add more later. If you're not sure how to phrase it, the goal builder has templates you can copy.",
      done: goalCount > 0,
      cta: (studentCount > 0 && goalCount === 0) ? {
        label: 'Add a goal',
        icon: 'Target',
        onClick: () => navigate('/iep-goals-and-objectives-tracking'),
      } : null,
    },
    {
      n: 3,
      title: 'Log data or a behavior incident',
      body: "This is the button you'll use every day. Tap Collect Data on this dashboard (or the round button in the bottom-right on your phone) any time something happens.",
      done: dataPointCount > 0 || incidentCount > 0,
      cta: (goalCount > 0 && dataPointCount === 0 && incidentCount === 0) ? {
        label: 'Collect data',
        icon: 'ClipboardEdit',
        onClick: onCollectDataClick,
      } : null,
    },
  ];

  return (
    <div className="mb-6 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
          <Icon name="Sparkles" size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">
            Get started in 3 steps
          </h2>
          <p className="text-sm text-gray-600 mt-0.5">
            No hurry — you can leave this and come back. The steps stay
            here until they're done.
          </p>
        </div>
      </div>

      <ol className="space-y-3">
        {steps.map((step) => (
          <li
            key={step.n}
            className={`flex items-start gap-3 rounded-lg border p-4 ${
              step.done
                ? 'border-green-200 bg-green-50/50'
                : 'border-gray-200 bg-white'
            }`}
          >
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                step.done
                  ? 'bg-green-500 text-white'
                  : 'bg-blue-100 text-blue-700'
              }`}
              aria-hidden="true"
            >
              {step.done ? <Icon name="Check" size={16} /> : step.n}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`font-medium ${
                  step.done ? 'text-green-800' : 'text-gray-900'
                }`}
              >
                {step.title}
                {step.done && (
                  <span className="ml-2 text-xs font-normal text-green-700">
                    ✓ done
                  </span>
                )}
              </p>
              <p
                className={`text-sm mt-0.5 ${
                  step.done ? 'text-green-700/80' : 'text-gray-600'
                }`}
              >
                {step.body}
              </p>
              {step.cta && (
                <div className="mt-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={step.cta.onClick}
                    iconName={step.cta.icon}
                    iconPosition="left"
                  >
                    {step.cta.label}
                  </Button>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default GetStartedGuide;
